import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { getRequest } from "../../../../config";

export default function ResearchAidReport({ company, tab }) {

    const location = useLocation();
    const dispatch = useDispatch();

    const modal = useSelector(state => state.modal);
    const getHeight = child => child.offsetHeight + parseFloat(getComputedStyle(child).marginTop) + parseFloat(getComputedStyle(child).marginBottom);

    // A4 dimensions in pixels (at 96 DPI)
    const A4_HEIGHT_PX = 1122; // 297mm at 96 DPI
    const A4_WIDTH_PX = 794; // 210mm at 96 DPI
    const PAGE_MARGIN_TOP = 48; // 3rem
    const PAGE_MARGIN_BOTTOM = 48; // 3rem
    const FOOTER_HEIGHT = 80; // Footer height including margins
    const HEADER_HEIGHT = 96; // Header height for continuation pages
    const AVAILABLE_HEIGHT = A4_HEIGHT_PX - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM - FOOTER_HEIGHT;

    let [reportId, setReportId] = useState();
    let [reportData, setReportData] = useState({});

    useEffect(() => {
        if (window.location.hash) reportId = window.location.hash.split('?')[0]
        if (reportId) {
            reportId = reportId.replaceAll('#', '')
            setReportId(reportId);
        }
    }, []);

    // Lazy load specific report from API
    useEffect(() => {
        const fetchReport = async () => {
            if (company?._id && reportId && tab) {
                try {
                    const response = await getRequest(`/company/${company._id}/reports`);
                    if (response['code'] == 200 && response['body']?.['reports']) {
                        // Merge both report arrays to find the report
                        const allReports = [ ...(response['body']['reports']['researchAidReports'] || []), ...(response['body']['reports']['customReports'] || []) ];
                        const report = allReports.find(item => item['_id'] === reportId);
                        if (report) setReportData(report);
                    }
                } catch (e) {
                    console.error('Failed to fetch report:', e);
                }
            }
        };
        fetchReport();
    }, [company?._id, reportId, tab]);

    useEffect(() => {
        const pdfContent = document.getElementById("pdfContent");
        const wrapper = document.getElementById("pdf");

        if (!pdfContent || !wrapper) return;

        pdfContent.classList.add("a4-pdf", "p3rem");
        wrapper.innerHTML = "";

        let children = Array.from(pdfContent.children);
        let pageNumber = 1;
        let currentPageHeight = 0;
        let contentContainer = null;

        // Helper function to create footer
        const createFooter = (pageNum) => {
            const footer = document.createElement("div");
            footer.classList.add("tac", "fw600", "df", "jcsb", "aife", "pa", "c36", "b10", "l0", "pdfFooterCustom");
            footer.style.bottom = "10px";
            footer.style.left = "0";
            footer.style.right = "0";
            footer.style.padding = "0 48px";

            const footerLogo = document.createElement("img");
            footerLogo.src = "https://repository-diligentsia.s3.ap-northeast-1.amazonaws.com/development/miniicon-1735284051094.png";
            footerLogo.alt = "miniicon";
            footerLogo.classList.add("h40px", "w40px", "tal");

            const trademark = document.createElement("p");
            trademark.innerText = `© Diligentsia Limited ${new Date().getFullYear()}`;
            trademark.classList.add("tac", "c37", "fs12px", "fw400");

            const pageNumberElem = document.createElement("h3");
            pageNumberElem.innerText = "Page " + pageNum;
            pageNumberElem.classList.add("fw600", "c36", "fs14px");

            footer.appendChild(footerLogo);
            footer.appendChild(trademark);
            footer.appendChild(pageNumberElem);

            return footer;
        };

        // Helper function to create a new page
        const createPage = (isFirstPage = false) => {
            const page = document.createElement("div");
            page.classList.add("a4-pdf", "oyh", "bg1", "bsbb", "oh", "pr", "px3rem", "shadow", "new-report", "researchaid-report");
            page.style.minHeight = A4_HEIGHT_PX + "px";
            page.style.maxHeight = A4_HEIGHT_PX + "px";
            page.style.position = "relative";

            // Add watermark
            const watermark = document.createElement("img");
            watermark.classList.add("op20", "pa");
            watermark.style.transform = "translate(-50%, -50%) rotate(30deg)";
            watermark.style.top = "50%";
            watermark.style.left = "50%";
            watermark.style.width = "297mm";
            watermark.style.zIndex = "0";
            page.appendChild(watermark);

            // Create content container
            const content = document.createElement("div");
            content.style.position = "relative";
            content.style.zIndex = "1";
            content.style.paddingBottom = FOOTER_HEIGHT + "px";

            // Add header for continuation pages
            if (!isFirstPage) {
                titleHeading(content);
                currentPageHeight = HEADER_HEIGHT;
            } else {
                currentPageHeight = 0;
            }

            page.appendChild(content);

            // Add footer
            const footer = createFooter(pageNumber);
            page.appendChild(footer);

            wrapper.appendChild(page);
            pageNumber++;

            return { page, content };
        };

        // Helper function to check if element fits on current page
        const fitsOnPage = (elementHeight) => {
            return currentPageHeight + elementHeight <= AVAILABLE_HEIGHT;
        };

        // Helper function to split large content elements
        const splitLargeElement = (element) => {
            const elementHeight = getHeight(element);

            // If element fits on page, just add it
            if (fitsOnPage(elementHeight)) {
                contentContainer.appendChild(element.cloneNode(true));
                currentPageHeight += elementHeight;
                return;
            }

            // For divs with multiple children, try to split by children
            if (element.tagName === 'DIV' && element.children.length > 0) {
                const children = Array.from(element.children);
                const wrapper = element.cloneNode(false);

                children.forEach(child => {
                    const childHeight = getHeight(child);

                    if (!fitsOnPage(childHeight)) {
                        // Add current wrapper if it has content
                        if (wrapper.children.length > 0) {
                            contentContainer.appendChild(wrapper);
                        }

                        // Create new page
                        const newPage = createPage();
                        contentContainer = newPage.content;

                        // Create new wrapper
                        wrapper.innerHTML = '';
                    }

                    wrapper.appendChild(child.cloneNode(true));
                    currentPageHeight += childHeight;
                });

                // Add remaining wrapper
                if (wrapper.children.length > 0) {
                    contentContainer.appendChild(wrapper);
                }
            } else {
                // For other elements or single large elements, add to new page
                if (currentPageHeight > 0) {
                    const newPage = createPage();
                    contentContainer = newPage.content;
                }

                contentContainer.appendChild(element.cloneNode(true));
                currentPageHeight += elementHeight;
            }
        };

        // Helper function to process table elements
        const processTable = (table) => {
            const rows = Array.from(table.querySelectorAll('tr'));
            const clonedTable = table.cloneNode(false);
            let tableHeader = null;

            // Check if table has thead
            const thead = table.querySelector('thead');
            if (thead) {
                tableHeader = thead.cloneNode(true);
            }

            rows.forEach((row, index) => {
                const rowHeight = getHeight(row);

                if (!fitsOnPage(rowHeight)) {
                    // Add current table to page if it has rows
                    if (clonedTable.querySelector('tr')) {
                        contentContainer.appendChild(clonedTable);
                    }

                    // Create new page
                    const newPage = createPage();
                    contentContainer = newPage.content;

                    // Start new table with header if exists
                    clonedTable = table.cloneNode(false);
                    if (tableHeader) {
                        clonedTable.appendChild(tableHeader.cloneNode(true));
                        currentPageHeight += getHeight(tableHeader);
                    }
                }

                clonedTable.appendChild(row.cloneNode(true));
                currentPageHeight += rowHeight;
            });

            // Add remaining table
            if (clonedTable.querySelector('tr')) {
                contentContainer.appendChild(clonedTable);
            }
        };

        // Create first page
        const firstPage = createPage(true);
        contentContainer = firstPage.content;

        // Process all children
        children.forEach((child, index) => {
            const childHeight = getHeight(child);

            // Handle tables specially
            if (child.tagName === 'TABLE') {
                processTable(child);
            } else if (childHeight > AVAILABLE_HEIGHT) {
                // Handle very large elements that need splitting
                splitLargeElement(child);
            } else {
                // Check if element fits on current page
                if (!fitsOnPage(childHeight)) {
                    // Create new page
                    const newPage = createPage();
                    contentContainer = newPage.content;
                }

                // Add element to current page
                contentContainer.appendChild(child.cloneNode(true));
                currentPageHeight += childHeight;
            }
        });

        // Clean up
        wrapper.classList.remove("op0", "pen");
        pdfContent.classList.remove("a4", "p3rem");
    });


    const titleHeading = (page) => {
        const title = document.createElement("span");
        title.classList.add("db", "fw600", "fs1-5rem", "c15", "ttu", "tal", "ass");

        title.classList.add("tal", "mb1-75rem");
        title.innerHTML = reportData['name'] + "<br><span class='db fw400 fs1-5rem c15 fsi tal ass mb1-5rem'>" + company['cName'] + "</span>";

        page.appendChild(title);
        return page;
    };

    const showReportData = () => {
        if (reportData?.['htmlReport']) {
            return <>
                <div className="df jcsb bg54 c15 mb3rem p2rem px2-5rem aic" style={{ margin: '-32px -48px 1rem', backgroundSize: '100% 100%', backgroundImage: `url(${require('../../../../../images/research-report-header-bg.jpg')})` }}>
                    <div class='df fdc'>
                        <h1 className="ass c15 fs1-75rem fw700">{company['cName']}</h1>
                        <h4 className="ass c15 fs1-25rem fw400 fsi">{reportData['name']}</h4>
                        <p className="fs0-75rem c15 mt1rem ass">Created By: {reportData?.['createdBy']?.['firstName']} {reportData?.['createdBy']?.['lastName']}</p>
                        <p className="fs0-75rem c15 ass">Report Date: <span className="fw600">{moment(reportData['createdAt']).format('ll')}</span></p>
                    </div>
                    <div class='df fdc pr3rem'>
                        <div className="progress-bar reasearcaid-report bg1 p0-5rem br100" style={{ width: 100, height: 100, border: '3px solid #f1f1f1' }}>
                            <CircularProgressbar
                                className={`${reportData['score'] >= 4 ? 'green' : reportData['score'] >= 2 ? 'orange' : 'red'}`}
                                value={reportData['score']}
                                strokeWidth={18}
                                text={reportData['score']}
                                styles={buildStyles({
                                    strokeLinecap: 'round',
                                    textSize: '20px',
                                    pathTransitionDuration: 0.5,
                                    trailColor: '#d6d6d6',
                                    backgroundColor: '#fc7785',
                                    dominantBaseline: 'ideographic',
                                })}
                            />
                        </div>
                        <p class='fs1rem fw400 c15 mt0-75rem'>Report Score</p>
                    </div>
                </div>

                {reportData?.['htmlReport'].map((item, idx) => {
                    return <div class={`c15 ${(company['subscription'] == 'Free' && company['cCategory'] == "client" && idx > 1) && ' blur-disabled'}`} dangerouslySetInnerHTML={{ __html: `${item}` }}></div>
                })}
            </>
        }

    };

    return <>
        {company && <>
            <table id="pdf" className={`op0 pen ma td250 dg gg2rem jcc`}></table>

            <table id="pdfContent" className="bsbb w0 h0 oh op0 pen db pr" style={{ width: "210mm" }}>
                {showReportData()}
            </table>
        </>}

    </>;
}